import React from 'react';
import { renderMiniMarkdown } from '../utils/miniMarkdown';
import { ASSET_STATUSES } from './ProjectAssetBoard';

// Overview tab: hero shot, model-work summary, progress digest
// (model-relevant items first), recent activity.

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hours = Math.floor(mins / 60);
  if (hours < 24) return hours + 'h ago';
  const days = Math.floor(hours / 24);
  if (days < 30) return days + 'd ago';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

function ProjectOverview({ user, project, assets, files, onNavigate, onPreview }) {
  const progress = project.progress;

  // Hero shot: latest featured image, falling back to latest image
  const images = files.filter(f => f.category === 'image');
  const hero = images.find(f => f.featured) || images[0] || null;

  // Model work summary
  const statusCounts = ASSET_STATUSES.map(s => ({
    ...s,
    count: assets.filter(a => a.status === s.key).length
  }));
  const totalAssets = assets.length;
  const wiredCount = statusCounts.find(s => s.key === 'wired')?.count || 0;

  // Recent activity: merge file uploads + asset updates
  const activity = [
    ...files.map(f => ({
      time: f.uploadedAt,
      icon: 'upload_file',
      text: `${f.uploadedBy === user.uid ? 'You' : (f.uploadedByName || 'Someone')} uploaded ${f.fileName}`,
      onClick: () => onPreview(f)
    })),
    ...assets.filter(a => a.updatedBy).map(a => {
      const meta = ASSET_STATUSES.find(s => s.key === a.status);
      return {
        time: a.updatedAt,
        icon: meta?.icon || 'view_in_ar',
        text: `${a.updatedBy === user.uid ? 'You' : (a.updatedByName || 'Someone')} moved ${a.name} to ${meta?.label || a.status}`,
        onClick: () => onNavigate('assets')
      };
    })
  ].filter(a => a.time).sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 8);

  const hasAnything = hero || totalAssets > 0 || progress || activity.length > 0;

  if (!hasAnything) {
    return (
      <div className="pv-empty-files">
        <span className="material-symbols-outlined">space_dashboard</span>
        <p>Nothing here yet</p>
      </div>
    );
  }

  return (
    <div className="pv-overview">
      {hero && (
        <div className="pv-overview-hero" onClick={() => onPreview(hero)}>
          <img src={hero.thumbUrl || hero.storageUrl} alt={hero.fileName} loading="lazy" decoding="async" />
          <div className="pv-overview-hero-caption">
            <span>{hero.fileName}</span>
            <span className="pv-overview-hero-date">{timeAgo(hero.uploadedAt)}</span>
          </div>
        </div>
      )}

      {progress?.headline && (
        <div className="pv-overview-headline">{progress.headline}</div>
      )}

      <div className="pv-overview-grid">
        {totalAssets > 0 && (
          <div className="pv-overview-card pv-overview-modelwork" onClick={() => onNavigate('assets')}>
            <div className="pv-overview-card-title">
              <span className="material-symbols-outlined">view_in_ar</span>
              Model Work
              <span className="pv-overview-card-sub">{wiredCount}/{totalAssets} in game</span>
            </div>
            <div className="pv-overview-status-rows">
              {statusCounts.filter(s => s.count > 0).map(s => (
                <div key={s.key} className="pv-overview-status-row">
                  <span className="material-symbols-outlined" style={{ color: s.color }}>{s.icon}</span>
                  <span className="pv-overview-status-label">{s.label}</span>
                  <span className="pv-overview-status-count">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {progress?.modelFocus && (
          <div className="pv-overview-card pv-overview-modelfocus">
            <div className="pv-overview-card-title accent">
              <span className="material-symbols-outlined">priority_high</span>
              Model Focus
            </div>
            <div className="pv-overview-md">{renderMiniMarkdown(progress.modelFocus)}</div>
          </div>
        )}

        {progress?.roadmap && (
          <div className="pv-overview-card">
            <div className="pv-overview-card-title">
              <span className="material-symbols-outlined">map</span>
              Roadmap
            </div>
            <div className="pv-overview-md">{renderMiniMarkdown(progress.roadmap)}</div>
          </div>
        )}

        {progress?.recent && (
          <div className="pv-overview-card">
            <div className="pv-overview-card-title">
              <span className="material-symbols-outlined">new_releases</span>
              Recent
            </div>
            <div className="pv-overview-md">{renderMiniMarkdown(progress.recent)}</div>
          </div>
        )}

        {activity.length > 0 && (
          <div className="pv-overview-card pv-overview-activity">
            <div className="pv-overview-card-title">
              <span className="material-symbols-outlined">history</span>
              Activity
            </div>
            {activity.map((a, i) => (
              <div key={i} className="pv-overview-activity-row" onClick={a.onClick}>
                <span className="material-symbols-outlined">{a.icon}</span>
                <span className="pv-overview-activity-text">{a.text}</span>
                <span className="pv-overview-activity-time">{timeAgo(a.time)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {progress?.updatedAt && (
        <div className="pv-overview-updated">
          Status updated {timeAgo(progress.updatedAt)}
        </div>
      )}
    </div>
  );
}

export default ProjectOverview;
